// src/pages/container_rental/components/timeline/TimelineHeader.tsx
//
// 날짜 눈금 행. 상단(월) / 하단(주 또는 일) 2단.
// 스크롤 컨테이너 안에서 position: sticky; top: 0 으로 고정된다(부모가 지정).
//
// 선·색은 확정 디자인 시스템의 뉴트럴 스케일을 따른다(구 웜 잉크 #0E0F12 의
// rgba 파생값과 1px solid 먹선은 모두 제거).
//  · 헤더 하단/밴드 구분선 #E4E7EA · 눈금 major #D7DBDF / minor #EDEFF1
//  · 날짜 라벨은 mono(tabular) 를 데이터 표기 용도로만 쓰고 자간 장식은 없다.

import { Box } from "@mui/material";
import { designTokens } from "../../../../theme.ts";
import { MARK } from "../statusVisuals.ts";
import type { Tick, TickKind } from "./TimelineScale.ts";

const T = designTokens;

/** 시스템 뉴트럴 — 타임라인 격자 전용 */
const LINE = {
  /** 헤더 하단 · 밴드 경계 */
  border: "#E4E7EA",
  /** 눈금 major */
  major: "#D7DBDF",
  /** 눈금 minor */
  minor: "#EDEFF1",
  /** 하단 밴드 면 — 흰 상단과 구분되도록 중립 톤 */
  band: "#F2F4F5",
  sub: "#6B7178",
} as const;

/** 일 눈금이 이보다 많으면 월요일만 그려서 DOM 폭주를 막는다. */
const DAY_TICK_LIMIT = 1200;

export const HEADER_TOP_H = 26;
export const HEADER_BOTTOM_H = 24;
export const HEADER_H = HEADER_TOP_H + HEADER_BOTTOM_H;

interface TimelineHeaderProps {
  width: number;
  top: Tick[];
  bottom: Tick[];
  bottomKind: TickKind;
  bottomLabelled: boolean;
  /** 오늘선 x(px). 도메인 밖이면 null. */
  todayX: number | null;
}

export default function TimelineHeader({
  width,
  top,
  bottom,
  bottomKind,
  bottomLabelled,
  todayX,
}: TimelineHeaderProps) {
  const bottomTicks =
    bottomKind === "day" && bottom.length > DAY_TICK_LIMIT
      ? bottom.filter((t) => t.major)
      : bottom;

  return (
    <Box
      sx={{
        position: "relative",
        width,
        flex: "0 0 auto",
        height: HEADER_H,
        bgcolor: T.paper,
        borderBottom: `1px solid ${LINE.border}`,
        userSelect: "none",
      }}
    >
      {/* 상단 — 월 */}
      <Box sx={{ position: "absolute", inset: `0 0 ${HEADER_BOTTOM_H}px 0` }}>
        {top.map((t) => (
          <Box
            key={t.key}
            sx={{
              position: "absolute",
              left: t.x,
              top: 0,
              width: t.width,
              height: HEADER_TOP_H,
              borderLeft: `1px solid ${t.major ? LINE.major : LINE.minor}`,
              display: "flex",
              alignItems: "center",
              px: 0.6,
              boxSizing: "border-box",
              overflow: "hidden",
              fontFamily: T.fonts.mono,
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 0,
              fontVariantNumeric: "tabular-nums",
              color: LINE.sub,
              whiteSpace: "nowrap",
            }}
          >
            {t.width >= 30 ? t.label : ""}
          </Box>
        ))}
      </Box>

      {/* 하단 — 주 또는 일 */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: HEADER_BOTTOM_H,
          borderTop: `1px solid ${LINE.border}`,
          bgcolor: LINE.band,
        }}
      >
        {bottomTicks.map((t) => (
          <Box
            key={t.key}
            sx={{
              position: "absolute",
              left: t.x,
              top: 0,
              width: t.width,
              height: HEADER_BOTTOM_H,
              borderLeft: `1px solid ${t.major ? LINE.major : LINE.minor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              overflow: "hidden",
              fontFamily: T.fonts.mono,
              fontSize: 9.5,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: LINE.sub,
              whiteSpace: "nowrap",
            }}
          >
            {bottomLabelled && t.width >= 16 ? t.label : ""}
          </Box>
        ))}
      </Box>

      {/* 오늘 라벨 — 오늘선의 머리 */}
      {todayX !== null && (
        <Box
          sx={{
            position: "absolute",
            left: todayX,
            top: 0,
            height: HEADER_H,
            borderLeft: `2px solid ${MARK.today}`,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 2,
              bgcolor: MARK.today,
              color: "#FFFFFF",
              borderRadius: "4px",
              fontFamily: T.fonts.sans,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0,
              px: 0.6,
              py: "2px",
              whiteSpace: "nowrap",
            }}
          >
            오늘
          </Box>
        </Box>
      )}
    </Box>
  );
}
